import React from 'react';
import { isNavigationRouteActive } from '@/lib/navigationRoute.mjs';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FiLogOut,
  FiChevronRight,
  FiChevronLeft,
  FiHome,
  FiPackage,
  FiBox,
  FiGrid,
  FiArchive,
  FiClipboard,
  FiSend,
  FiLayers,
  FiDatabase,
  FiUsers,
  FiList,
  FiTag,
  FiActivity,
  FiAirplay,
  FiBriefcase,
  FiCoffee,
  FiMapPin,
  FiShoppingBag,
  FiCompass,
  FiCalendar,
} from 'react-icons/fi';
import { ChevronDown, Search } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarSeparator,
  useSidebar,
} from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';

export const WORKSPACE_NAV = [
  {
    id: 'home',
    label: 'Home',
    icon: FiHome,
    path: '/admin',
    alwaysVisible: true,
  },
  {
    id: 'package-manager',
    label: 'Product Manager',
    icon: FiPackage,
    path: '/admin/package-manager/overview',
    children: [
      { label: 'Overview', path: '/admin/package-manager/overview', icon: FiGrid },
      { label: 'My Products', path: '/admin/package-manager/my-packages', icon: FiBox },
      { label: 'Product Catalog', path: '/admin/package-manager/product-catalog', icon: FiGrid },
      { label: 'Templates', path: '/admin/package-manager/templates', icon: FiArchive },
      { label: 'Drafts & Readiness', path: '/admin/package-manager/drafts-readiness', icon: FiClipboard },
      { label: 'Integrasi API Mitra', path: '/admin/package-manager/integrations', icon: FiLayers },
      { label: 'Monitor Transaksi API', path: '/admin/package-manager/channel-transactions', icon: FiActivity },
    ],
  },
  {
    id: 'product-monitoring',
    label: 'Product Monitoring',
    icon: FiActivity,
    path: '/admin/product-monitoring/products',
    children: [
      { label: 'Overview', path: '/admin/product-monitoring/overview', icon: FiGrid },
      { label: 'Products & Packages', path: '/admin/product-monitoring/products', icon: FiBox },
      { label: 'All Bookings', path: '/admin/product-monitoring/reservations', icon: FiCalendar },
      { label: 'Transaction Pipeline', path: '/admin/product-monitoring/reservations?view=pipeline', icon: FiActivity },
      { label: 'Needs Attention', path: '/admin/product-monitoring/reservations?view=attention', icon: FiActivity },
      { label: 'Channel Performance', path: '/admin/product-monitoring/channels', icon: FiActivity },
      { label: 'Members', path: '/admin/product-monitoring/members', icon: FiUsers },
    ],
  },
  {
    id: 'ieem-workspace',
    label: 'IEEM Workspace',
    icon: FiSend,
    path: '/admin/ieem-dashboard',
    children: [
      { label: 'Airports', path: '/admin/airports/ieem', icon: FiAirplay },
      { label: 'Aviation Services', path: '/admin/aviation-services/ieem', icon: FiBriefcase },
      { label: 'Hospitality', path: '/admin/hospitality/ieem', icon: FiCoffee },
      { label: 'Destination Management', path: '/admin/destination-management/ieem', icon: FiMapPin },
      { label: 'Retail', path: '/admin/retail/ieem', icon: FiShoppingBag },
      { label: 'Tourism Development', path: '/admin/tourism-development/ieem', icon: FiCompass },
    ],
  },
  {
    id: 'domain-workspace',
    label: 'Product Workspace',
    icon: FiLayers,
    path: '/admin/product-dashboard',
    children: [
      { label: 'Airports', path: '/admin/airports/bundling-product', icon: FiAirplay },
      { label: 'Aviation Services', path: '/admin/aviation-services/bundling-product', icon: FiBriefcase },
      { label: 'Hospitality', path: '/admin/hospitality/bundling-product', icon: FiCoffee },
      { label: 'Destination Management', path: '/admin/destination-management/bundling-product', icon: FiMapPin },
      { label: 'Retail', path: '/admin/retail/bundling-product', icon: FiShoppingBag },
      { label: 'Tourism Development', path: '/admin/tourism-development/bundling-product', icon: FiCompass },
    ],
  },
  {
    id: 'internal-tools',
    label: 'Internal Tools',
    icon: FiActivity,
    path: '/admin/internal-tools/purchase-simulator',
    children: [
      { label: 'Purchase Simulator', path: '/admin/internal-tools/purchase-simulator', icon: FiShoppingBag },
      { label: 'API Documentation', path: '/admin/internal-tools/api-documentation', icon: FiClipboard },
      { label: 'Member Integration', path: '/admin/internal-tools/member-integration', icon: FiActivity },
      { label: 'FTP Hotel Mapping', path: '/admin/internal-tools/ftp-hotel-mapping', icon: FiDatabase },
      { label: 'Integration Settings', path: '/admin/internal-tools/integration-settings', icon: FiActivity },
    ],
  },
  {
    id: 'master-data',
    label: 'Master Data',
    icon: FiDatabase,
    path: '/admin/master-data/menu',
    children: [
      { label: 'Users', path: '/admin/manage-users', icon: FiUsers },
      { label: 'Menus', path: '/admin/master-data/menu', icon: FiList },
      { label: 'Menu Categories', path: '/admin/master-data/menu-category', icon: FiTag },
      { label: 'Menu Sub-Categories', path: '/admin/master-data/menu-subcategory', icon: FiTag },
      { label: 'Activity Log', path: '/admin/master-data/user-activity-log', icon: FiActivity },
    ],
  },
];

export const filterNavByPermission = (nav, isPathAllowed) => {
  const filterChildren = (children = []) => children
    .map((child) => {
      if (child.children?.length) {
        const allowedChildren = filterChildren(child.children);
        if (isPathAllowed(child.path) || allowedChildren.length > 0) {
          return {
            ...child,
            path: isPathAllowed(child.path) ? child.path : allowedChildren[0].path,
            children: allowedChildren,
          };
        }
        return null;
      }
      return isPathAllowed(child.path) ? child : null;
    })
    .filter(Boolean);

  return nav
    .map((item) => {
      if (item.alwaysVisible) {
        return { ...item, children: undefined };
      }
      if (item.children?.length) {
        const allowedChildren = filterChildren(item.children);
        if (allowedChildren.length === 0) {
          return null;
        }
        const parentPath = isPathAllowed(item.path) ? item.path : allowedChildren[0].path;
        return { ...item, path: parentPath, children: allowedChildren };
      }
      return isPathAllowed(item.path) ? item : null;
    })
    .filter(Boolean);
};

const menuButtonActiveClass =
  'data-[active=true]:bg-[#E5E8EC] data-[active=true]:text-[#121212] data-[active=true]:font-semibold';

function useIsRouteActive() {
  const location = useLocation();
  return React.useCallback((path) => {
    return isNavigationRouteActive(path, location);
  }, [location.pathname, location.search]);
}

function buildSearchEntries(visibleNav) {
  const entries = [];
  const addChildren = (children, group, ParentIcon) => {
    for (const ch of children) {
      const ChildIcon = ch.icon || ParentIcon;
      entries.push({
        key: ch.path,
        title: ch.label,
        path: ch.path,
        group,
        Icon: ChildIcon,
      });
      if (ch.children?.length) {
        addChildren(ch.children, `${group} / ${ch.label}`, ChildIcon);
      }
    }
  };

  for (const item of visibleNav) {
    if (!item.children?.length) {
      entries.push({
        key: item.path,
        title: item.label,
        path: item.path,
        group: null,
        Icon: item.icon,
      });
    } else {
      entries.push({
        key: `${item.id}-root`,
        title: `${item.label} — overview`,
        path: item.path,
        group: item.label,
        Icon: item.icon,
      });
      addChildren(item.children, item.label, item.icon);
    }
  }
  return entries;
}

function navNodeActive(node, isRouteActive) {
  return isRouteActive(node.path) || Boolean(node.children?.some((child) => navNodeActive(child, isRouteActive)));
}

function SidebarNestedChild({ child, ParentIcon, isRouteActive, onNavigate }) {
  const ChildIcon = child.icon || ParentIcon;
  const active = navNodeActive(child, isRouteActive);
  const [open, setOpen] = React.useState(active);

  React.useEffect(() => {
    if (active) setOpen(true);
  }, [active]);

  if (!child.children?.length) {
    return (
      <SidebarMenuSubItem key={child.path}>
        <SidebarMenuSubButton
          isActive={active}
          onClick={() => onNavigate(child.path)}
          className={cn(active && 'bg-[#E5E8EC] font-semibold text-[#121212]')}
        >
          <ChildIcon className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">{child.label}</span>
        </SidebarMenuSubButton>
      </SidebarMenuSubItem>
    );
  }

  return (
    <SidebarMenuSubItem key={child.path}>
      <Collapsible open={open} onOpenChange={setOpen} className="group/nested">
        <div className="flex items-center gap-0.5">
          <SidebarMenuSubButton
            isActive={active}
            onClick={() => onNavigate(child.path)}
            className={cn('min-w-0 flex-1', active && 'bg-[#E5E8EC] font-semibold text-[#121212]')}
          >
            <ChildIcon className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{child.label}</span>
          </SidebarMenuSubButton>
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-sidebar-foreground hover:bg-sidebar-accent"
              aria-label={`Toggle ${child.label}`}
            >
              <ChevronDown className={cn('h-3.5 w-3.5 transition-transform duration-200', open && 'rotate-180')} />
            </button>
          </CollapsibleTrigger>
        </div>
        <CollapsibleContent>
          <SidebarMenuSub className="ml-4 border-l border-sidebar-border pl-2">
            {child.children.map((grandChild) => (
              <SidebarNestedChild
                key={grandChild.path}
                child={grandChild}
                ParentIcon={ChildIcon}
                isRouteActive={isRouteActive}
                onNavigate={onNavigate}
              />
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </Collapsible>
    </SidebarMenuSubItem>
  );
}

function NavItemWithChildren({ item, isRouteActive, onNavigate, state, isMobile }) {
  const hasActive = navNodeActive(item, isRouteActive);
  const [open, setOpen] = React.useState(hasActive);

  React.useEffect(() => {
    if (hasActive) setOpen(true);
  }, [hasActive]);

  const parentActive = isRouteActive(item.path) || hasActive;
  const Icon = item.icon;

  if (state === 'collapsed' && !isMobile) {
    return (
      <SidebarMenuItem key={item.id}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton isActive={parentActive} className={menuButtonActiveClass} title={item.label}>
              <Icon className="h-4 w-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" align="start" sideOffset={8} className="w-56">
            <DropdownMenuLabel className="font-semibold text-foreground">{item.label}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="cursor-pointer gap-2 font-medium" onClick={() => onNavigate(item.path)}>
              <Icon className="h-4 w-4" />
              Open {item.label}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {item.children.map((child) => {
              const ChildIcon = child.icon || Icon;
              const childActive = navNodeActive(child, isRouteActive);
              return (
                <DropdownMenuItem
                  key={child.path}
                  className={cn('cursor-pointer gap-2', childActive && 'bg-accent text-accent-foreground')}
                  onClick={() => onNavigate(child.path)}
                >
                  <ChildIcon className="h-4 w-4 shrink-0" />
                  {child.label}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    );
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="group/collapsible">
      <SidebarMenuItem>
        <div className="flex w-full min-w-0 items-stretch gap-0.5">
          <SidebarMenuButton
            isActive={parentActive}
            onClick={() => onNavigate(item.path)}
            className={cn('min-w-0 flex-1 pr-0', menuButtonActiveClass)}
          >
            <Icon className="h-4 w-4 shrink-0" />
            <span className="truncate">{item.label}</span>
          </SidebarMenuButton>
          <CollapsibleTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0 text-sidebar-foreground hover:bg-sidebar-accent"
              aria-label={`Toggle ${item.label}`}
            >
              <ChevronDown
                className={cn('h-4 w-4 transition-transform duration-200', open && 'rotate-180')}
              />
            </Button>
          </CollapsibleTrigger>
        </div>
        <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
          <SidebarMenuSub>
            {item.children.map((child) => (
              <SidebarNestedChild
                key={child.path}
                child={child}
                ParentIcon={Icon}
                isRouteActive={isRouteActive}
                onNavigate={onNavigate}
              />
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}

const AppSidebar = ({ onLogout }) => {
  const navigate = useNavigate();
  const isRouteActive = useIsRouteActive();
  const { isPathAllowed, isMenuLoading, allowedPaths, user } = useAuth();
  const { state, isMobile, openMobile, setOpen, setOpenMobile, toggleSidebar } = useSidebar();

  const [searchOpen, setSearchOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const searchInputRef = React.useRef(null);

  React.useEffect(() => {
    if (openMobile) {
      setOpen(true);
    }
  }, [openMobile, setOpen]);

  React.useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        const tag = (e.target && e.target.tagName) || '';
        if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) return;
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  React.useEffect(() => {
    if (searchOpen) {
      setSearchQuery('');
      const t = requestAnimationFrame(() => searchInputRef.current?.focus());
      return () => cancelAnimationFrame(t);
    }
  }, [searchOpen]);

  const visibleNav = React.useMemo(() => {
    if (isMenuLoading && allowedPaths.size === 0) {
      return [];
    }
    return filterNavByPermission(WORKSPACE_NAV, isPathAllowed);
  }, [isMenuLoading, allowedPaths, isPathAllowed, user]);

  const searchEntries = React.useMemo(() => buildSearchEntries(visibleNav), [visibleNav]);

  const filteredSearch = React.useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return searchEntries;
    return searchEntries.filter(
      (e) =>
        e.title.toLowerCase().includes(q) ||
        (e.group && e.group.toLowerCase().includes(q))
    );
  }, [searchEntries, searchQuery]);

  const handleNavigate = React.useCallback(
    (path) => {
      navigate(path, { replace: false });
      setOpenMobile(false);
      setSearchOpen(false);
    },
    [navigate, setOpenMobile]
  );

  const modKey =
    typeof navigator !== 'undefined' && /Mac|iPhone|iPod|iPad/i.test(navigator.platform || '')
      ? '⌘'
      : 'Ctrl+';

  return (
    <>
      <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
        <DialogContent className="max-w-md gap-0 p-0" hideClose>
          <DialogHeader className="border-b px-4 py-3 text-left">
            <DialogTitle className="text-base">Cari menu</DialogTitle>
          </DialogHeader>
          <div className="p-3">
            <Input
              ref={searchInputRef}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Ketik nama menu…"
              className="h-10"
              aria-label="Filter menu"
            />
          </div>
          <div className="max-h-[min(60vh,360px)] overflow-y-auto border-t px-2 py-2">
            {filteredSearch.length === 0 ? (
              <p className="px-2 py-6 text-center text-sm text-muted-foreground">Tidak ada hasil</p>
            ) : (
              <ul className="flex flex-col gap-0.5">
                {filteredSearch.map((entry) => {
                  const EntryIcon = entry.Icon;
                  return (
                    <li key={entry.key}>
                      <button
                        type="button"
                        onClick={() => handleNavigate(entry.path)}
                        className={cn(
                          'flex w-full items-center gap-2 rounded-md px-3 py-2.5 text-left text-sm transition-colors',
                          'hover:bg-accent hover:text-accent-foreground',
                          isRouteActive(entry.path) && 'bg-accent font-medium'
                        )}
                      >
                        <EntryIcon className="h-4 w-4 shrink-0 opacity-70" />
                        <span className="min-w-0 flex-1 truncate">{entry.title}</span>
                        {entry.group && (
                          <span className="shrink-0 text-xs text-muted-foreground">{entry.group}</span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Sidebar collapsible="icon" variant="sidebar" className="z-[1000] border-sidebar-border shadow-[0px_4px_23px_rgba(0,0,0,0.06)]">
        <SidebarHeader className="gap-3 border-b border-sidebar-border px-2 pb-2 pt-5 sm:pt-6 group-data-[collapsible=icon]:px-1 group-data-[collapsible=icon]:pb-2 group-data-[collapsible=icon]:pt-2.5">
          {state === 'collapsed' && !isMobile ? (
            <div className="flex w-full flex-col items-center gap-1.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-md bg-[#E5E8EC] text-[11px] font-semibold text-[#121212]">
                IJ
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0 text-sidebar-foreground hover:bg-sidebar-accent"
                onClick={toggleSidebar}
                aria-label="Buka sidebar"
              >
                <FiChevronRight className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <div className="relative w-full px-1">
              {!isMobile && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-1/2 z-10 h-8 w-8 -translate-y-1/2 text-sidebar-foreground hover:bg-sidebar-accent"
                  onClick={toggleSidebar}
                  aria-label="Ciutkan sidebar"
                >
                  <FiChevronLeft className="h-4 w-4" />
                </Button>
              )}
              <div className="flex justify-center pr-10">
                <div className="mx-auto block text-center">
                  <p className="text-[18px] font-semibold leading-none tracking-tight text-[#121212]">InJourney</p>
                  <p className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#6B7280]">Inventory</p>
                </div>
              </div>
            </div>
          )}

          {(state === 'expanded' || isMobile) && (
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              className="relative w-full rounded-md border border-transparent text-left outline-none ring-sidebar-ring focus-visible:ring-2"
            >
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <div className="flex h-9 w-full items-center rounded-md border border-input bg-muted/40 pl-9 pr-16 text-sm text-muted-foreground">
                Cari menu…
              </div>
              <kbd className="pointer-events-none absolute right-2 top-1/2 hidden -translate-y-1/2 select-none items-center gap-0.5 rounded border bg-background px-1.5 font-mono text-[10px] font-medium text-muted-foreground sm:inline-flex">
                {modKey}K
              </kbd>
            </button>
          )}
        </SidebarHeader>

        <SidebarContent className="gap-0 px-0">
          <SidebarGroup>
            <SidebarGroupLabel className="px-4">Workspace</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu className="px-2">
                {visibleNav.length === 0 ? (
                  <div className="mx-2 rounded-lg border border-dashed border-sidebar-border p-4 text-center">
                    <p className="text-xs text-muted-foreground">
                      {state === 'collapsed' && !isMobile ? '—' : 'Tidak ada menu yang tersedia'}
                    </p>
                  </div>
                ) : (
                  visibleNav.map((item) => {
                    if (!item.children?.length) {
                      const Icon = item.icon;
                      const active = isRouteActive(item.path);
                      return (
                        <SidebarMenuItem key={item.id}>
                          <SidebarMenuButton
                            isActive={active}
                            tooltip={item.label}
                            onClick={() => handleNavigate(item.path)}
                            className={menuButtonActiveClass}
                          >
                            <Icon className="h-4 w-4" />
                            <span className="truncate">{item.label}</span>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      );
                    }
                    return (
                      <NavItemWithChildren
                        key={item.id}
                        item={item}
                        isRouteActive={isRouteActive}
                        onNavigate={handleNavigate}
                        state={state}
                        isMobile={isMobile}
                      />
                    );
                  })
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>

        <SidebarSeparator />

        <SidebarFooter className="gap-2 p-3">
          {user && (state === 'expanded' || isMobile) && (
            <div className="truncate px-1 text-xs text-muted-foreground">
              <p className="truncate font-medium text-sidebar-foreground">{user.name || user.email || 'User'}</p>
              {user.email && <p className="truncate">{user.email}</p>}
            </div>
          )}
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                onClick={onLogout}
                tooltip="Keluar"
                className="text-red-600 hover:bg-red-50 hover:text-red-700 data-[active=true]:bg-red-50"
              >
                <FiLogOut className="h-4 w-4" />
                <span>Keluar</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>
    </>
  );
};

export default AppSidebar;
